import { FC } from "react"
import {
  Box,
  useTheme,
  Paper,
  Avatar,
  Typography,
  Stack,
  Divider,
  Button,
  Tabs,
  Tab,
} from "@mui/material"
import clsx from "clsx"

import { IFeatureDetailResult } from "hooks/feature/useFeatureDetail"
import { getIcon } from "utils/icon"

import { InfoItem } from "extensions/one-siam/components/InfoItem"
import {
  ClockIcon,
  LayerIcon,
  ShopIcon,
} from "extensions/one-siam/components/icons"
import {
  PlaceDetailTabPanel,
  GalleryCarousel,
  PromotionList,
  SkeletonPlaceDetailCard,
} from "extensions/one-siam/components/domains/features"

import { QRCode } from "components/QRCode"
import { useTranslation } from "providers/i18n"
import { IAmenityDetail, IOccupantDetail, ISectionDetail } from "utils/feature"
import { useDetailTab } from "extensions/one-siam/hooks"

const TAB_INDEXES = {
  GALLERY: "gallery-tab",
  PROMOTION: "promotion-tab",
}

export interface PlaceDetailCardProps {
  data: IFeatureDetailResult &
    IOccupantDetail &
    ISectionDetail &
    IAmenityDetail & { open?: string; close?: string }
  mobileUrl?: string
  onNavigate: () => void
  canNavigate?: boolean
  loaded?: boolean
}

export const PlaceDetailCard: FC<PlaceDetailCardProps> = ({
  data,
  mobileUrl = null,
  onNavigate,
  canNavigate = false,
  loaded = false,
}) => {
  const {
    category,
    parentCategoryRefs,
    localParentCategoryRefs,
    description,
    gallery = [],
    level,
    logo,
    name,
    promotions,
    venueName,
    categoryIconProp = {},
    open,
    close,
  } = data || {}

  const {
    showGallery,
    showPromotion,
    showDetailTabs,
    detailTabProps,
    selectedDetailTab,
    changeDetailTab,
  } = useDetailTab({
    data,
    enabled: loaded,
  })

  const { t } = useTranslation()
  const theme = useTheme()
  const {
    container,
    primarySection,
    secondarySection,
    innerSection,
    headerContainer,
    descriptionContainer,
    avatarContainer,
    occupantLogo,
    detailContainer,
    listInfoContainer,
    listInfoDivider,
    listInfoItem,
    divider,
    titleText,
    descriptionText,
    qrCode,
    qrCodeContainer,
    qrCodeHelperText,
    actionContainer,
    actionButton,
    // Tabs
    placeDetailTabContainer,
    placeDetailTabs,
    placeDetailTabPanel,
    placeDetailTab,
  } = theme.components[`PlaceDetailCard`]?.styleOverrides || {}

  const { galleryCarouselOption } =
    theme.components[`PlaceDetailCard`]?.defaultProps || {}

  if (!loaded) return <SkeletonPlaceDetailCard />

  return (
    <Paper
      sx={container}
      className={clsx(parentCategoryRefs, localParentCategoryRefs)}
    >
      <Box sx={primarySection}>
        <Box sx={innerSection}>
          <Box sx={headerContainer}>
            <Box sx={avatarContainer}>
              <Avatar sx={occupantLogo} src={logo}>
                {getIcon(categoryIconProp, null, {
                  imageIconProps: {
                    solidColorFilterId: "placeDetail_avatar_filter",
                  },
                }) || <ShopIcon />}
              </Avatar>
            </Box>
            <Box sx={detailContainer}>
              <Typography sx={titleText} variant="h5">
                {t(name)}
              </Typography>
              <Stack
                sx={listInfoContainer}
                direction="row"
                spacing={2}
                divider={
                  <Divider
                    className="PlaceDetailCard-listInfoDivider"
                    sx={listInfoDivider}
                    orientation="vertical"
                    flexItem
                  />
                }
              >
                <InfoItem
                  className={clsx("venue", "PlaceDetailCard-listInfoItem")}
                  sx={listInfoItem}
                  label={t(venueName)}
                />
                {category && (
                  <InfoItem
                    className={clsx("category", "PlaceDetailCard-listInfoItem")}
                    sx={listInfoItem}
                    label={t(category)}
                  />
                )}
              </Stack>
              <Stack
                sx={listInfoContainer}
                direction="row"
                spacing={2}
                divider={
                  <Divider
                    className="PlaceDetailCard-listInfoDivider"
                    sx={listInfoDivider}
                    orientation="vertical"
                    flexItem
                  />
                }
              >
                <InfoItem
                  className="PlaceDetailCard-listInfoItem"
                  sx={listInfoItem}
                  icon={<LayerIcon />}
                  label={t(level)}
                />
                <InfoItem
                  className="PlaceDetailCard-listInfoItem"
                  sx={listInfoItem}
                  icon={<ClockIcon />}
                  label={`${open} - ${close}`}
                />
              </Stack>
            </Box>
          </Box>
          <Divider className="PlaceDetailCard-divider" sx={divider} />
          <Box sx={descriptionContainer}>
            <Typography
              variant="body1"
              className="PlaceDetailCard-descriptionText"
              sx={descriptionText}
            >
              {t(description)}
            </Typography>
          </Box>
        </Box>
        <Box sx={placeDetailTabContainer}>
          {showDetailTabs && (
            <>
              <Tabs
                sx={placeDetailTabs}
                className="PlaceDetailCard-placeDetailTabs"
                value={selectedDetailTab}
                onChange={changeDetailTab}
                indicatorColor="primary"
                variant="fullWidth"
              >
                <Tab
                  sx={placeDetailTab}
                  className={clsx("PlaceDetailCard-placeDetailTab", {
                    "tab-hidden": !showGallery,
                  })}
                  label={t("Gallery")}
                  value={TAB_INDEXES.GALLERY}
                />
                <Tab
                  sx={placeDetailTab}
                  className={clsx("PlaceDetailCard-placeDetailTab", {
                    "tab-hidden": !showPromotion,
                  })}
                  label={t("Promotion")}
                  value={TAB_INDEXES.PROMOTION}
                />
              </Tabs>
              {showGallery && (
                <PlaceDetailTabPanel
                  sx={placeDetailTabPanel}
                  value={selectedDetailTab}
                  {...detailTabProps.gallery}
                >
                  <GalleryCarousel
                    images={gallery}
                    carouselOptions={galleryCarouselOption}
                  />
                </PlaceDetailTabPanel>
              )}
              {showPromotion && (
                <PlaceDetailTabPanel
                  sx={placeDetailTabPanel}
                  value={selectedDetailTab}
                  {...detailTabProps.promotion}
                >
                  <PromotionList data={promotions} />
                </PlaceDetailTabPanel>
              )}
            </>
          )}
        </Box>
      </Box>
      <Box sx={secondarySection} className="PlaceDetailCard-secondarySection">
        {mobileUrl && (
          <Box sx={qrCodeContainer}>
            <QRCode sx={qrCode} url={mobileUrl} />
            <Stack>
              <Typography sx={qrCodeHelperText}>
                {t("Scan for direction")}
              </Typography>
              <Typography sx={qrCodeHelperText}>{t("On mobile")}</Typography>
            </Stack>
          </Box>
        )}
        <Box sx={actionContainer}>
          <Button
            variant="contained"
            onClick={onNavigate}
            className="PlaceDetailCard-actionButton"
            sx={{
              ...actionButton,
              visibility: canNavigate ? "visible" : "hidden",
            }}
          >
            {t("Get direction")}
          </Button>
        </Box>
      </Box>
    </Paper>
  )
}
